<script setup lang="ts">
import { OrgValidateOtp, useToast } from "@chatfood/design-system";
import { ref, onMounted } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import { AuthRepoError } from "@chatfood/dbe-client/errors";
import type { ISignInCustomerProps } from "./props";
import type { ISignInCustomerEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";
import { OtpDeliverySelector } from "~/components/otp-delivery-selector";
import { useBugReporter, useDirectBackend } from "#imports";

const props = defineProps<ISignInCustomerProps>();
const emits = defineEmits<ISignInCustomerEmits>();

const { t } = useLocale();
const { report } = useBugReporter();
const { toastError, toastUnexpectedError } = useToast();

const css = bemBuilder("sign-in-customer");

const business = useBusinessStore();
const customer = useCustomerStore();

const { authRepo } = useDirectBackend();

const view = ref<"otp-request" | "otp-validation">(
  props.skipOtpSelector ? "otp-validation" : "otp-request",
);

const isVerifyingCode = ref(false);
const errorMessage = ref<string>("");

const sendOtpViaEmail = async () => {
  if (!props.skipOtpSelector) return;

  try {
    await authRepo.requestOtp({
      businessId: business.id,
      messagingChannel: "email",
      key: "email",
      email: props.email,
    });
  } catch (err: unknown) {
    report(err);
    toastError(t("components.sign_in_customer.error_unknown"));
    emits("failure");
  }
};

onMounted(async () => {
  if (props.skipOtpSelector) {
    await sendOtpViaEmail();
  }
});

const onSignIn = async (verificationCode: string) => {
  errorMessage.value = "";
  isVerifyingCode.value = true;

  try {
    await authRepo.signIn({
      email: props.email,
      verificationCode,
    });

    await onAuthSuccess();
  } catch (err: unknown) {
    isVerifyingCode.value = false;

    if (err instanceof AuthRepoError) {
      if (err.code === "validation" || err.code === "businessRule") {
        errorMessage.value = t("components.sign_in_customer.error_invalid_otp");
        return;
      }

      if (err.code === "tooManyRequests") {
        toastError(
          t("components.sign_in_customer.error_too_many_otp_validation"),
        );
        emits("failure");
      }

      return;
    }

    errorMessage.value =
      err instanceof Error
        ? err.message
        : t("components.sign_in_customer.error_unknown");

    report(err);
  }
};

const onReset = () => {
  view.value = "otp-request";
  isVerifyingCode.value = false;
  errorMessage.value = "";
};

const onAuthSuccess = async () => {
  try {
    await customer.loadProfile(business.id);
    emits("success");
  } catch (e) {
    report(e);

    const errorMesssage =
      e instanceof Error ? e.message : typeof e === "string" ? e : undefined;

    if (errorMesssage) {
      toastError(errorMesssage);
    } else {
      toastUnexpectedError();
    }

    isVerifyingCode.value = false;
  }
};

const isEmailSelected = ref(false);
const hasManuallySelectedOtp = ref(false);

const onOtpSent = (channel: string) => {
  view.value = "otp-validation";
  isEmailSelected.value = channel === "email";
  hasManuallySelectedOtp.value = true;
};
</script>

<template>
  <div :class="css('')">
    <OtpDeliverySelector
      v-if="view === 'otp-request'"
      :email="props.email"
      :phone-number="props.phoneNumber"
      :throttle-seconds="props.throttleSeconds"
      @success="onOtpSent"
      @failure="() => emits('failure')"
    />
    <OrgValidateOtp
      v-if="view === 'otp-validation'"
      :title="t('components.sign_in_customer.request_otp_title')"
      :is-loading="isVerifyingCode"
      :show-close-button="false"
      :headline="t('components.sign_in_customer.request_otp_subtitle')"
      :phone-number="
        props.skipOtpSelector && !hasManuallySelectedOtp
          ? props.email
          : isEmailSelected
            ? props.email
            : props.phoneNumber
      "
      :resend-throttle-sec="props.throttleSeconds"
      :error-message="errorMessage"
      :resend-message="t('components.sign_in_customer.resend_otp_label')"
      :otp-length="5"
      :cta="t('components.sign_in_customer.request_otp_cta')"
      @validate="(val) => onSignIn(val.pinCode)"
      @resend="onReset"
    />
  </div>
</template>
